import type { BoundaryPositionV12, CustomBehaviorV12 } from "../shared/questionnaire-v12";
import type { ClientUnit, DraftDocument, ValidationIssue } from "./types";

const CONCERNS = "F01.high_impact_behaviors";
const CUSTOM = "F01.custom_behaviors";
const POSITIONS = "F01.boundary_positions";
export const BOUNDARY_LABELS: Readonly<Record<BoundaryPositionV12["state"], string>> = {
  discussable: "很在意，但可以沟通",
  non_negotiable: "这是不可协商的边界",
  unsure: "还不确定",
  unanswered: "暂不回答",
};

export function boundaryAnswers(document: DraftDocument) {
  const answers = document.answers.F01 ?? {};
  const selected: unknown = answers[CONCERNS];
  const custom: unknown = answers[CUSTOM];
  const positions: unknown = answers[POSITIONS];
  return {
    selected: Array.isArray(selected) ? selected.filter((value): value is string => typeof value === "string") : [],
    custom: Array.isArray(custom) ? custom.filter((value): value is CustomBehaviorV12 => Boolean(value) && typeof value === "object" && typeof value.id === "string" && typeof value.text === "string") : [],
    positions: positions && !Array.isArray(positions) && typeof positions === "object" ? positions as Readonly<Record<string, BoundaryPositionV12>> : {},
  };
}

export function PreviousQuestionnaireAnswers({ document, unit, scope, reading = false, unanswered = false }: { document: Pick<DraftDocument, "migration">; unit?: ClientUnit; scope: "F01" | "F06"; reading?: boolean; unanswered?: boolean }) {
  const migration = document.migration;
  const labels = new Map(unit?.fields.find(({ id }) => id === CONCERNS)?.options?.map(({ id, label }) => [id, label]) ?? []);
  labels.set("none_absolute", "我不认为这些行为都必然无法修复");
  labels.set("other", "其他");
  const record = (scope === "F01" ? migration?.legacyF01 : migration?.legacyF06) ?? {};
  const fields = scope === "F01" ? [
    ["F01.high_impact_behaviors", "之前选择的高影响行为"],
    ["F01.hard_boundary_authorizations", "之前选择的不可协商边界"],
    ["otherText", "之前补充的原话"],
  ] : [
    ["F06.temporal_change", "关于过去和现在的变化"],
    ["F06.system_misread", "关于可能被误解的地方"],
    ["F06.unmodeled_condition", "关于未被表达的现实条件"],
  ];
  const rows = fields.flatMap(([key, label]) => {
    const value = record[key];
    const text = typeof value === "string" ? value : Array.isArray(value) ? value.filter((item) => typeof item === "string").map((id) => labels.get(id) ?? id).join(reading ? "、" : "；") : "";
    return text ? [{ key, label, text }] : [];
  });
  if (!rows.length) return reading && unanswered ? <p className="r4-answer-skipped">暂不回答</p> : null;
  return <details className="previous-answers"><summary>{reading && unanswered && <>暂不回答 · </>}之前填写的回答{reading && <span className="r4-previous-chevron" aria-hidden="true"> ›</span>}</summary><dl>{rows.map(({ key, label, text }) => <div key={key}><dt>{label}</dt><dd>{text}</dd></div>)}</dl></details>;
}

export function QuestionnaireBoundaryStep({ unit, document, issues, onChange }: {
  unit: ClientUnit;
  document: DraftDocument;
  issues: ValidationIssue[];
  onChange: (document: DraftDocument) => void;
}) {
  const { selected, custom, positions } = boundaryAnswers(document);
  const concernField = unit.fields.find((field) => field.id === CONCERNS)!;
  const options = concernField.options ?? [];
  const rows = [
    ...options.filter(({ id }) => selected.includes(id)).map(({ id, label }) => ({ id, text: label, custom: false })),
    ...custom.filter(({ text }) => text.trim().length > 0).map(({ id, text }) => ({ id, text, custom: true })),
  ];
  const update = (next: { selected?: readonly string[]; custom?: readonly CustomBehaviorV12[]; positions?: Readonly<Record<string, BoundaryPositionV12>> }) => {
    onChange({ ...document, answers: { ...document.answers, F01: {
      ...document.answers.F01,
      [CONCERNS]: [...(next.selected ?? selected)],
      [CUSTOM]: next.custom ?? custom,
      [POSITIONS]: next.positions ?? positions,
    } } });
  };
  const removePosition = (id: string) => Object.fromEntries(Object.entries(positions).filter(([key]) => key !== id));
  const boundaryIssues = [...new Map(issues.filter(({ path }) => path.includes("F01") || path === "f01Reviewed")
    .map((issue) => [`${issue.path}-${issue.message}`, issue])).values()];
  return <article className="question-sheet">
    {document.f01Reviewed === false && <p className="field-note">这一页的选项已更新，请重新确认。其他回答已保留。</p>}
    <PreviousQuestionnaireAnswers document={document} unit={unit} scope="F01" />
    <fieldset className="question-field">
      <legend>{concernField.prompt}<span className="required-mark"> 可不填</span></legend>
      <div className="option-list">
        {options.map((option) => <label className="option-row" key={option.id}>
          <input type="checkbox" checked={selected.includes(option.id)} onChange={(event) => update(event.target.checked
            ? { selected: [...selected, option.id] }
            : { selected: selected.filter((id) => id !== option.id), positions: removePosition(option.id) })} />
          <span>{option.label}</span>
        </label>)}
      </div>
      {custom.map((item, index) => <div className="boundary-custom" key={item.id}>
        <label htmlFor={`custom-${item.id}`}>其他 {custom.length > 1 ? index + 1 : ""}</label>
        <textarea id={`custom-${item.id}`} value={item.text} rows={3} maxLength={1200}
          onChange={(event) => update({ custom: custom.map((value) => value.id === item.id ? { ...value, text: event.target.value } : value) })} />
        <button className="text-button" type="button" onClick={() => update({ custom: custom.filter(({ id }) => id !== item.id), positions: removePosition(item.id) })}>移除这一项</button>
      </div>)}
      <button type="button" className="text-button inline-action" onClick={() => update({ custom: [...custom, { id: `custom-${crypto.randomUUID()}`, text: "" }] })}>{custom.length ? "再补充一项" : "其他"}</button>
    </fieldset>

    {rows.length > 0 && <section className="boundary-positions" aria-labelledby="boundary-positions-prompt">
      <h2 id="boundary-positions-prompt" className="question-prompt">对刚才选的每一项，分别更接近哪种想法？<span className="required-mark"> 可不填</span></h2>
      {rows.map((row) => {
        const position = positions[row.id];
        const stale = row.custom && position && position.state !== "unanswered" && position.text !== row.text;
        return <fieldset className="question-field boundary-item" key={row.id}>
          <legend>{row.text}</legend>
          {stale && <p className="field-note" role="status">这项内容已修改，请重新选择，也可以暂不回答。</p>}
          <div className="option-list compact">
            {Object.entries(BOUNDARY_LABELS).map(([state, label]) => <label className="option-row" key={state}>
              <input type="radio" name={`boundary-${row.id}`} checked={!stale && position?.state === state}
                onChange={() => update({ positions: { ...positions, [row.id]: { state: state as BoundaryPositionV12["state"], ...(row.custom ? { text: row.text } : {}) } } })} />
              <span>{label}</span>
            </label>)}
          </div>
        </fieldset>;
      })}
    </section>}
    {boundaryIssues.map((issue) => <p className="error-message" role="alert" key={`${issue.path}-${issue.message}`}>{issue.message}</p>)}
  </article>;
}
